import React, { useState } from 'react';
import { Space, Switch, Modal, Input, Button, Popconfirm, Tag, message, Alert, Tooltip } from 'antd';
import { KeyOutlined, ReloadOutlined, CopyOutlined } from '@ant-design/icons';
import {
  useResetUserPassword,
  useResetUserTotp,
  useSetUserTotpRequired,
  generatePassword,
  MIN_PASSWORD_LENGTH,
} from '../hooks/useAccountSecurity';

/**
 * ช่อง "รหัสผ่าน / 2FA" ในตารางจัดการผู้ใช้ — ใช้ร่วมกันทั้ง
 *   /admin/employees      (apiBase = /api/v1/users)
 *   /backoffice/employees (apiBase = /api/v1/usersbackoffice)
 *
 * รวมสามอย่างที่ admin ต้องใช้บ่อยไว้ที่เดียว ไม่ต้องเปิดฟอร์มแก้ไข:
 *   1. สวิตช์เปิด/ปิด 2FA รายคน
 *   2. ตั้งรหัสผ่านใหม่ให้ (พร้อมปุ่มสุ่มรหัส)
 *   3. รีเซ็ต 2FA (กรณีทำมือถือหาย/เปลี่ยนเครื่อง)
 *
 * record ต้องมี id, totp_required, hasTotp — hasTotp ฝั่ง backend คำนวณให้จาก
 * totp_secret แล้วตัด secret ทิ้งก่อนส่งมา (functions/sanitizeUser.js)
 */
const AccountSecurityCell = ({ record, apiBase, onDone }) => {
  const [pwOpen, setPwOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savedPassword, setSavedPassword] = useState(null); // โชว์หลังบันทึกสำเร็จ ให้ copy ส่งต่อ

  const { mutateAsync: resetPassword, isLoading: isSavingPw } = useResetUserPassword(apiBase);
  const { mutateAsync: resetTotp, isLoading: isResettingTotp } = useResetUserTotp(apiBase);
  const { mutateAsync: setTotpRequired, isLoading: isTogglingTotp } = useSetUserTotpRequired(apiBase);

  // ระบบเป็น opt-in: ปิดไว้เป็นค่าเริ่มต้น เปิดเฉพาะที่ค่าเป็น 1 ชัดเจน
  // ต้องตรงกับ isTotpRequired() ฝั่ง backend เป๊ะ ๆ ไม่งั้นสวิตช์จะโชว์ไม่ตรงกับที่ระบบใช้จริง
  const totpOn = record?.totp_required === 1 || record?.totp_required === '1' || record?.totp_required === true;

  const closePwModal = () => {
    setPwOpen(false);
    setPassword('');
    setConfirmPassword('');
    setSavedPassword(null);
  };

  const handleToggleTotp = async (checked) => {
    try {
      await setTotpRequired({ id: record.id, required: checked });
      message.success(checked ? 'เปิดใช้ 2FA แล้ว' : 'ปิดใช้ 2FA แล้ว — คนนี้ login ด้วยรหัสผ่านอย่างเดียว');
      onDone?.();
    } catch (error) {
      message.error(error?.response?.data?.message || 'ตั้งค่าไม่สำเร็จ');
    }
  };

  const handleResetTotp = async () => {
    try {
      await resetTotp({ id: record.id });
      message.success('รีเซ็ต 2FA แล้ว — ครั้งถัดไปที่ login จะมี QR ใหม่ให้สแกน');
      onDone?.();
    } catch (error) {
      message.error(error?.response?.data?.message || 'รีเซ็ตไม่สำเร็จ');
    }
  };

  const handleSavePassword = async () => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      message.warning(`รหัสผ่านต้องยาวอย่างน้อย ${MIN_PASSWORD_LENGTH} ตัวอักษร`);
      return;
    }
    if (password !== confirmPassword) {
      message.warning('รหัสผ่านทั้งสองช่องไม่ตรงกัน');
      return;
    }
    try {
      await resetPassword({ id: record.id, password });
      setSavedPassword(password);   // ยังไม่ปิด modal — ให้ admin copy ไปส่งให้เจ้าตัวก่อน
      message.success('ตั้งรหัสผ่านใหม่สำเร็จ');
      onDone?.();
    } catch (error) {
      message.error(error?.response?.data?.message || 'ตั้งรหัสผ่านใหม่ไม่สำเร็จ');
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(savedPassword);
      message.success('คัดลอกแล้ว');
    } catch (error) {
      message.warning('คัดลอกอัตโนมัติไม่ได้ กรุณาเลือกข้อความแล้วคัดลอกเอง');
    }
  };

  return (
    <>
      <Space size="small" wrap>
        <Tooltip title={totpOn ? 'ปิดเพื่อให้ login ด้วยรหัสผ่านอย่างเดียว' : 'เปิดเพื่อบังคับใช้ Google Authenticator'}>
          <Switch
            size="small"
            checked={totpOn}
            loading={isTogglingTotp}
            onChange={handleToggleTotp}
            checkedChildren="2FA"
            unCheckedChildren="2FA"
          />
        </Tooltip>

        {totpOn && (
          <Tag color={record?.hasTotp ? 'green' : 'orange'} style={{ marginInlineEnd: 0 }}>
            {record?.hasTotp ? 'ผูกแล้ว' : 'รอสแกน QR'}
          </Tag>
        )}

        <Button type="link" size="small" icon={<KeyOutlined />} onClick={() => setPwOpen(true)}>
          ตั้งรหัสใหม่
        </Button>

        <Popconfirm
          title="รีเซ็ต 2FA"
          description="ล้างการผูกแอปเดิม ครั้งถัดไปที่ login จะได้ QR ใหม่ให้สแกน"
          okText="ยืนยัน"
          cancelText="ยกเลิก"
          onConfirm={handleResetTotp}
        >
          <Button type="link" size="small" danger icon={<ReloadOutlined />} loading={isResettingTotp}
                  disabled={!record?.hasTotp}>
            รีเซ็ต 2FA
          </Button>
        </Popconfirm>
      </Space>

      <Modal
        title={`ตั้งรหัสผ่านใหม่ให้ ${record?.UserName ?? ''}`}
        open={pwOpen}
        onCancel={closePwModal}
        maskClosable={false}
        footer={
          savedPassword
            ? [<Button key="close" type="primary" onClick={closePwModal}>เสร็จสิ้น</Button>]
            : [
                <Button key="cancel" onClick={closePwModal}>ยกเลิก</Button>,
                <Button key="ok" type="primary" loading={isSavingPw} disabled={isSavingPw}
                        onClick={handleSavePassword}>
                  บันทึก
                </Button>,
              ]
        }
      >
        {savedPassword ? (
          <Alert
            type="success"
            showIcon
            message="ตั้งรหัสผ่านใหม่เรียบร้อย"
            description={
              <div>
                <p className="mb-2">ส่งรหัสนี้ให้เจ้าตัว แล้วบอกให้เปลี่ยนเป็นรหัสของตัวเองหลัง login</p>
                <Space>
                  <Input readOnly value={savedPassword} style={{ fontFamily: 'monospace', width: 220 }} />
                  <Button icon={<CopyOutlined />} onClick={copyPassword}>คัดลอก</Button>
                </Space>
              </div>
            }
          />
        ) : (
          <div className="flex flex-col gap-3">
            <div>
              <div className="text-sm text-gray-600 mb-1">รหัสผ่านใหม่</div>
              <Space.Compact style={{ width: '100%' }}>
                <Input.Password
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`อย่างน้อย ${MIN_PASSWORD_LENGTH} ตัวอักษร`}
                  autoFocus
                />
                <Button onClick={() => {
                  const generated = generatePassword();
                  setPassword(generated);
                  setConfirmPassword(generated);
                }}>
                  สุ่มรหัส
                </Button>
              </Space.Compact>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">ยืนยันรหัสผ่านใหม่</div>
              <Input.Password
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onPressEnter={handleSavePassword}
                placeholder="พิมพ์ซ้ำอีกครั้ง"
              />
            </div>
            <div className="text-xs text-gray-400">
              การตั้งรหัสใหม่ไม่กระทบ 2FA — ถ้าเจ้าตัวเปลี่ยนมือถือด้วย ต้องกด "รีเซ็ต 2FA" แยกอีกที
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AccountSecurityCell;
